/**
 * TranscriptSheet — pull-up overlay above the dock (TASK-711, Spotlight
 * pattern). Renders the scope's transcript (bubbles + proposal cards + tool
 * progress) on pages whose native surface isn't the canvas/rail. Overlay, not
 * layout: pages keep their own scroll; the sheet floats above content and
 * below the dock bar.
 */

import React, { useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { useCanvasStore, CANVAS_DRAWER_GEOMETRY } from '../../stores/canvasStore';

export interface TranscriptSheetProps {
  open: boolean;
  onClose: () => void;
  /** Scope label shown in the sheet header, e.g. "KYC · Maria Muster". */
  scopeLabel?: string;
  /** TASK-766: the target of the scope chip's `aria-controls`. */
  id?: string;
  children: React.ReactNode;
}

export const TranscriptSheet: React.FC<TranscriptSheetProps> = ({
  open,
  onClose,
  scopeLabel,
  id,
  children,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const drawerOpen = useCanvasStore((s) => s.drawerOpen);

  // Esc collapses the sheet (never navigates).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Follow the conversation: stick to the bottom on new content.
  useEffect(() => {
    if (open && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [open, children]);

  if (!open) return null;

  return (
    <div
      className={`fixed bottom-14 left-0 right-0 mx-auto max-w-3xl px-4 ${
        // TASK-768: the CanvasDrawer is a right-anchored slide-over at z-40. It
        // used to overlap this sheet and clip the conversation mid-sentence.
        // Where there is room for both, the sheet sits entirely left of it;
        // below that, the sheet layers ABOVE it — never behind, which is the
        // failure being fixed.
        //
        // TASK-790: the whole z-index lives in this ternary. It used to also
        // carry a base `z-30`, so the open state emitted `z-30 z-50 xl:z-30` in
        // one class list and resolved correctly only because Tailwind happens to
        // emit `z-50` after `z-30` and media queries last. Correct by
        // construction now, not by stylesheet order.
        drawerOpen
          ? `z-50 xl:z-30 ${CANVAS_DRAWER_GEOMETRY.stepAside}`
          : 'z-30'
      }`}
      id={id}
      data-testid="transcript-sheet"
      data-drawer-open={drawerOpen ? 'true' : undefined}
      role="dialog"
      aria-label={scopeLabel ? `Conversation — ${scopeLabel}` : 'Conversation'}
    >
      <div className="flex max-h-[55vh] flex-col overflow-hidden rounded-t-xl border border-b-0 border-border bg-background/98 shadow-2xl backdrop-blur">
        <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            {scopeLabel ?? 'Conversation'}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Collapse conversation"
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
        <div ref={bodyRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
          {children}
        </div>
      </div>
    </div>
  );
};
